"use client";

import type { FC } from "react";
import Image from "next/image";
import { ChevronRightIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useAskQuestionData } from "@/contexts/HomeDataContext";
import type { FAQItem } from "@/types/homePage";
import { FadeIn, Stagger } from "@/components/ui/scroll-animated";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Constants
const ANIMATION_DELAYS = {
  tagline: 200,
  heading: 300,
  accordion: 400,
  stagger: 100,
  staggerStart: 500,
  image: 600,
} as const;

const IMAGE_CONFIG = {
  src: "/images/faq-section.png",
  alt: "Modern data analytics and technology innovation",
  sizes: "(max-width: 768px) 85vw, (max-width: 1200px) 570px, 570px",
} as const;

const LAYOUT_STYLES = {
  background: {
    width: 'clamp(200px, 60vw, 410px)',
    height: '100%',
  },
  imageContainer: {
    width: 'clamp(280px, 85vw, 570px)',
    height: 'calc(100% - 34px)',
  },
} as const;

// Sub-components
const SectionTagline: FC = () => {
  const t = useTranslations("FAQ");

  return (
    <FadeIn delay={ANIMATION_DELAYS.tagline}>
      <div className="bg-brand-gradient-text bg-clip-text text-transparent font-h1-up font-[number:var(--h1-up-font-weight)] text-[length:var(--h1-up-font-size)] tracking-[var(--h1-up-letter-spacing)] leading-[var(--h1-up-line-height)] [font-style:var(--h1-up-font-style)]">
        {t("tagline")}
      </div>
    </FadeIn>
  );
};

const SectionHeading: FC = () => {
  const t = useTranslations("FAQ");
  const headingLines = t("heading").split("\n");

  return (
    <FadeIn delay={ANIMATION_DELAYS.heading}>
      <h2 className="font-heading-h1 font-[number:var(--heading-h1-font-weight)] text-x-900 text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-[length:var(--heading-h1-font-size)] tracking-[var(--heading-h1-letter-spacing)] leading-tight md:leading-tight lg:leading-[var(--heading-h1-line-height)] [font-style:var(--heading-h1-font-style)]">
        {headingLines.map((line, index) => (
          <span key={`heading-line-${index}`}>
            {line}
            {index < headingLines.length - 1 && <br />}
          </span>
        ))}
      </h2>
    </FadeIn>
  );
};

interface FAQAccordionItemProps {
  item: FAQItem;
  index: number;
}

const FAQAccordionItem: FC<FAQAccordionItemProps> = ({ item, index }) => {
  const t = useTranslations("FAQ");

  const handleAccordionClick = () => {
    // Console log for debugging
    console.log('FAQ Item Clicked:', {
      id: item.id,
      question: t(`items.${item.id}.question`),
      position: index + 1,
    });

    // Track FAQ accordion interaction with Google Analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'faq_item_click', {
        event_category: 'FAQ',
        event_label: item.id,
        faq_question: t(`items.${item.id}.question`),
        value: index + 1,
      });
    }
  };

  return (
    <Stagger
      delay={ANIMATION_DELAYS.staggerStart}
      staggerDelay={ANIMATION_DELAYS.stagger}
      index={index}
    >
      <div data-faq-id={item.id}>
        <AccordionItem
          value={item.id}
          className="border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-xl hover:scale-[1.02] hover:border-blue-200 transition-all duration-300 ease-out group"
        >
          <AccordionTrigger 
            onClick={handleAccordionClick}
            className="px-6 py-5 hover:no-underline [&[data-state=open]>svg]:rotate-90 group-hover:bg-gradient-to-r group-hover:from-blue-50 group-hover:to-purple-50 transition-all duration-300"
          >
          <span className="font-heading-h4 font-[number:var(--heading-h4-font-weight)] text-x-900 text-[length:var(--heading-h4-font-size)] tracking-[var(--heading-h4-letter-spacing)] leading-[var(--heading-h4-line-height)] [font-style:var(--heading-h4-font-style)] text-left pr-4 group-hover:text-blue-700 transition-colors duration-300">
            {t(`items.${item.id}.question`)}
          </span>
          <ChevronRightIcon className="h-4 w-4 shrink-0 bg-brand-gradient-text bg-clip-text text-transparent transition-all duration-300 group-hover:scale-125 group-hover:rotate-12" />
        </AccordionTrigger>
        <AccordionContent className="px-6 pb-5 animate-fade-in">
          <div className="text-gray-600 font-body-m font-[number:var(--body-m-font-weight)] text-[length:var(--body-m-font-size)] tracking-[var(--body-m-letter-spacing)] leading-[var(--body-m-line-height)] [font-style:var(--body-m-font-style)] animate-slide-in-bottom">
            {t(`items.${item.id}.answer`)}
          </div>
        </AccordionContent>
      </AccordionItem>
      </div>
    </Stagger>
  );
};

interface FAQAccordionProps {
  items: FAQItem[];
}

const FAQAccordion: FC<FAQAccordionProps> = ({ items }) => (
  <FadeIn delay={ANIMATION_DELAYS.accordion}>
    <Accordion type="single" collapsible defaultValue="item-1" className="space-y-4">
      {items.map((item, index) => (
        <FAQAccordionItem key={item.id} item={item} index={index} />
      ))}
    </Accordion>
  </FadeIn>
);

const FAQImage: FC = () => (
  <div className="relative h-[400px] sm:h-[500px] md:h-[600px] lg:h-[664px] rtl:lg:order-1">
    <FadeIn delay={ANIMATION_DELAYS.image} className="h-full">
      <div className="relative h-full flex items-center justify-center">
        {/* Background gradient */}
        <div
          className="absolute top-0 right-0 rtl:left-0 rtl:right-auto rounded-[5px] bg-brand-gradient-diagonal"
          style={LAYOUT_STYLES.background}
        />

        {/* Image container */}
        <div
          className="absolute top-[17px] sm:top-[25px] md:top-[30px] lg:top-[34px] left-0 rtl:right-0 rtl:left-auto rounded-[5px] bg-white shadow-lg"
          style={LAYOUT_STYLES.imageContainer}
        >
          <div className="relative w-full h-full rounded-[5px] overflow-hidden">
            <Image
              src={IMAGE_CONFIG.src}
              alt={IMAGE_CONFIG.alt}
              fill
              className="object-cover"
              sizes={IMAGE_CONFIG.sizes}
              priority={false}
            />
          </div>
        </div>
      </div>
    </FadeIn>
  </div>
);

export const AskQuestionSection: FC = () => {
  const data = useAskQuestionData();

  return (
    <section className="relative w-full py-16 md:py-20 lg:py-24 bg-white overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Content */}
          <div className="space-y-8 rtl:lg:order-2">
            <div className="space-y-4">
              <SectionTagline />
              <SectionHeading />
            </div>
            <FAQAccordion items={data.faqItems} />
          </div>

          {/* Right Image Section */}
          <FAQImage />
        </div>
      </div>
    </section>
  );
};

